import { z } from 'zod';
import { UsdAmount } from './ai.js';
/**
 * Biểu đồ từ bảng trong `.chilldoc` ("Data insight") — APM-CH-b,
 * docs/plan-doc-charts.md §3, §6.
 *
 * Ba thứ tách bạch:
 *
 * - `ChartSpec` — cái AI (skill `suggest_charts`) hoặc "Tạo biểu đồ (không AI)"
 *   sinh ra: loại, cột nhãn, các series, cách gộp. **Không có số** — con số do
 *   hàm thuần ở web tính từ bảng, như `DiagramSpec` không có toạ độ. Tránh AI
 *   bịa số, và là điều kiện của liên kết sống.
 * - `ChartAttrs` — thuộc tính của node `chart` trong tệp: spec + nguồn (bảng nào,
 *   vùng nào) + bản chụp số lần lưu gần nhất. Kiểm **lỏng** (`.catch`) như
 *   `DrawingAttrs`: tệp của bản sau mở được ở bản trước.
 * - `ChartPlanInput` / `ChartPlan` / `ChartPlanResult` — hợp đồng của
 *   `POST /me/files/chart-plan`.
 *
 * Schema chỉ kiểm **hình dạng**. Luật chéo (cột trong vùng, bánh đúng một
 * series, nhãn trục bắt buộc…) ở `chartSpecIssues` — hàm thuần để `finalize`
 * của skill và web dùng chung một danh sách lỗi.
 */
// ---------------------------------------------------------------------------
// Hằng số
// ---------------------------------------------------------------------------
export const CHART_TYPES = [
    'bar',
    'stacked_bar',
    'horizontal_bar',
    'line',
    'area',
    'pie',
    'donut',
    'scatter',
    'combo',
];
export const ChartType = z.enum(CHART_TYPES);
/** Loại không có trục (lát bánh) — không cần `xLabel`/`yLabel`, đúng một series. */
export const CHART_PIE_TYPES = ['pie', 'donut'];
/** Loại có trục — `xLabel`/`yLabel` bắt buộc (plan §3.2: nhãn chiều dữ liệu ngay trên hình). */
export const CHART_AXIS_TYPES = CHART_TYPES.filter((type) => !CHART_PIE_TYPES.includes(type));
/** Tên loại biểu đồ bằng tiếng Việt — markdown đọc được (§3.4) và prompt của skill. */
export const CHART_TYPE_LABELS = {
    bar: 'cột',
    stacked_bar: 'cột chồng',
    horizontal_bar: 'thanh ngang',
    line: 'đường',
    area: 'vùng',
    pie: 'bánh',
    donut: 'vành khuyên',
    scatter: 'phân tán',
    combo: 'kết hợp cột–đường',
};
export const CHART_AGGREGATES = ['none', 'sum', 'avg', 'count', 'min', 'max'];
export const ChartAggregate = z.enum(CHART_AGGREGATES);
export const CHART_SORTS = ['none', 'value_desc', 'value_asc', 'category'];
export const ChartSort = z.enum(CHART_SORTS);
export const CHART_MAX_SERIES = 6;
export const CHART_TITLE_MAX = 80;
export const CHART_INSIGHT_MAX = 240;
/** Nhãn series và nhãn trục. */
export const CHART_LABEL_MAX = 60;
/** Top N: `limit` nằm trong khoảng này. */
export const CHART_LIMIT = { min: 2, max: 50 };
/** Số cột / hàng tối đa của một bảng mà chỉ số trong spec được trỏ tới. */
export const CHART_MAX_TABLE_COLUMNS = 100;
export const CHART_MAX_TABLE_ROWS = 10_000;
/** Số điểm vẽ tối đa — `chartData` ở web cắt ở đây (plan §4.4), bản chụp cũng vậy. */
export const CHART_MAX_POINTS = 200;
export const CHART_WIDTH = { min: 240, max: 1600, default: 640 };
export const CHART_HEIGHT = { min: 160, max: 900, default: 300 };
/** Trần bản chụp của một biểu đồ trong tệp: `JSON.stringify(snapshot)`. */
export const MAX_CHART_SNAPSHOT_BYTES = 100_000;
/** Markdown đọc được in tối đa chừng này hàng của bản chụp (§3.4). */
export const CHART_MARKDOWN_ROWS = 30;
/** Số biểu đồ mỗi lượt Data insight — người dùng chốt 2026-09-28. */
export const CHART_PLAN_COUNT = { min: 1, max: 6, default: 3 };
/** TSV của vùng gửi cho AI: ≤ 200 hàng × 20 cột, ≤ 20 000 ký tự (dư ⇒ web cắt đều, `sampled`). */
export const CHART_PLAN_MAX_ROWS = 200;
export const CHART_PLAN_MAX_ROW_COLUMNS = 20;
export const CHART_PLAN_ROWS_MAX_CHARS = 20_000;
export const CHART_PLAN_CAPTION_MAX = 1500;
export const CHART_PLAN_DOCUMENT_MAX = 8000;
export const CHART_PLAN_EXISTING_MAX = 20;
export const CHART_PLAN_PROMPT_MAX = 500;
export const CHART_PLAN_NOTE_MAX = 300;
/** Điểm chạm dữ liệu (APM-CH-i): tối đa 5 câu, mỗi câu ≤ 240 ký tự. */
export const CHART_FINDINGS_MAX = 5;
export const CHART_FINDING_MAX = 240;
// ---------------------------------------------------------------------------
// ChartSpec
// ---------------------------------------------------------------------------
const ColumnIndex = z
    .number()
    .int()
    .min(0)
    .max(CHART_MAX_TABLE_COLUMNS - 1);
const RowIndex = z
    .number()
    .int()
    .min(0)
    .max(CHART_MAX_TABLE_ROWS - 1);
// `.trim()` **trước** `.min()`/`.max()`: chuỗi toàn dấu cách không được lọt qua (bẫy CLAUDE.md).
const AxisLabel = z.string().trim().min(1).max(CHART_LABEL_MAX);
/** Vùng trong bảng — chỉ số 0, bao cả hai đầu, theo `TableMap` (hàng tiêu đề cũng tính). */
export const ChartRange = z
    .object({
    startRow: RowIndex,
    startCol: ColumnIndex,
    endRow: RowIndex,
    endCol: ColumnIndex,
})
    .refine((range) => range.startRow <= range.endRow && range.startCol <= range.endCol, {
    message: 'Vùng phải có đầu ≤ cuối.',
});
export const ChartSeries = z.object({
    /** Chỉ số cột trong **bảng** (không phải trong vùng). `count` thì cột nhãn cũng được. */
    column: ColumnIndex,
    aggregate: ChartAggregate,
    /** `null` ⇒ lấy tiêu đề cột. */
    label: AxisLabel.nullable(),
    /** Chỉ cho `combo` (bắt buộc ở đó); loại khác bỏ qua. */
    kind: z.enum(['bar', 'line']).nullable(),
    /** Trục phải — chỉ `combo` hai đơn vị. */
    axis: z.enum(['left', 'right']),
});
/**
 * Mọi trường **bắt buộc** (có thể `null`): đây cũng là schema nộp kết quả của
 * model — trường tuỳ chọn là chỗ model hay bỏ sót (như `DiagramSpec`).
 *
 * `xLabel` là nhãn trục **nhãn/nhóm** (cột `category`), `yLabel` là nhãn trục
 * **giá trị** — theo vai trò, không theo hướng: `horizontal_bar` vẽ `xLabel`
 * ở trục dọc. Nhờ vậy đổi `bar` ↔ `horizontal_bar` trong hộp sửa không phải
 * đổi chỗ hai nhãn. Scatter: `xLabel` là trục X (cột `category`, số).
 */
export const ChartSpec = z.object({
    type: ChartType,
    title: z.string().trim().min(1).max(CHART_TITLE_MAX),
    /** Một câu "biểu đồ này cho thấy gì" — AI viết, hiện nghiêng dưới hình. */
    insight: z.string().trim().min(1).max(CHART_INSIGHT_MAX).nullable(),
    /** Cột nhãn (trục nhóm / lát bánh); scatter: cột X. `null` ⇒ mỗi hàng một điểm theo thứ tự. */
    category: ColumnIndex.nullable(),
    series: z.array(ChartSeries).min(1).max(CHART_MAX_SERIES),
    /** Gộp các hàng cùng nhãn — mọi series phải có `aggregate ≠ 'none'`. */
    groupByCategory: z.boolean(),
    /** `value_*` sắp theo series đầu tiên. */
    sort: ChartSort,
    /** Top N; bánh/vành khuyên dư thì gộp thành "Khác". */
    limit: z.number().int().min(CHART_LIMIT.min).max(CHART_LIMIT.max).nullable(),
    xLabel: AxisLabel.nullable(),
    yLabel: AxisLabel.nullable(),
});
export const CHART_SPEC_ISSUE_CODES = [
    'column_out_of_table',
    'column_out_of_range',
    'pie_series_count',
    'category_required',
    'x_label_missing',
    'y_label_missing',
    'group_without_category',
    'group_needs_aggregate',
    'aggregate_without_group',
    'combo_series_count',
    'combo_kind_missing',
    'right_axis_not_combo',
    'series_duplicate',
];
/**
 * Lỗi chéo của một spec — rỗng là hợp lệ. `finalize` của skill dùng để từ chối
 * cho model sửa, web dùng để chặn nút Chèn/Lưu trong hộp sửa.
 *
 * Chỉ những luật **không cần dữ liệu**. Luật cần hồ sơ cột (cột số mà hồ sơ
 * không phải số, bánh có giá trị âm, scatter với X không phải số) và luật
 * giữa nhiều biểu đồ (trùng — `chartSignature`) là việc của nơi có dữ liệu.
 *
 * @param columnCount số cột của bảng nguồn.
 * @param range vùng người dùng khoanh; không truyền / `null` ⇒ cả bảng.
 */
export function chartSpecIssues(spec, columnCount, range) {
    const issues = [];
    const push = (code, path, message) => issues.push({ code, path, message });
    const checkColumn = (column, path) => {
        if (column >= columnCount) {
            push('column_out_of_table', path, `Cột ${column} nằm ngoài bảng (bảng có ${columnCount} cột, chỉ số 0–${columnCount - 1}).`);
        }
        else if (range && (column < range.startCol || column > range.endCol)) {
            push('column_out_of_range', path, `Cột ${column} nằm ngoài vùng đã chọn (cột ${range.startCol}–${range.endCol}).`);
        }
    };
    if (spec.category !== null)
        checkColumn(spec.category, 'category');
    spec.series.forEach((series, index) => checkColumn(series.column, `series.${index}.column`));
    const pie = CHART_PIE_TYPES.includes(spec.type);
    if (pie && spec.series.length !== 1) {
        push('pie_series_count', 'series', `Biểu đồ ${CHART_TYPE_LABELS[spec.type]} phải có đúng 1 series (đang có ${spec.series.length}).`);
    }
    if ((pie || spec.type === 'scatter') && spec.category === null) {
        push('category_required', 'category', spec.type === 'scatter'
            ? 'Biểu đồ phân tán cần cột X (`category`, cột số).'
            : `Biểu đồ ${CHART_TYPE_LABELS[spec.type]} cần cột nhãn cho các lát (\`category\`).`);
    }
    if (!pie) {
        if (spec.xLabel === null)
            push('x_label_missing', 'xLabel', 'Thiếu nhãn trục nhóm (`xLabel`).');
        if (spec.yLabel === null) {
            push('y_label_missing', 'yLabel', 'Thiếu nhãn trục giá trị (`yLabel`).');
        }
    }
    if (spec.groupByCategory) {
        if (spec.category === null) {
            push('group_without_category', 'groupByCategory', 'Gộp theo nhãn cần có cột nhãn.');
        }
        spec.series.forEach((series, index) => {
            if (series.aggregate === 'none') {
                push('group_needs_aggregate', `series.${index}.aggregate`, 'Gộp theo nhãn thì mỗi series phải có cách gộp (sum/avg/count/min/max), không phải "none".');
            }
        });
    }
    else {
        spec.series.forEach((series, index) => {
            if (series.aggregate !== 'none') {
                push('aggregate_without_group', `series.${index}.aggregate`, `Cách gộp "${series.aggregate}" chỉ có nghĩa khi gộp theo nhãn (\`groupByCategory: true\`).`);
            }
        });
    }
    if (spec.type === 'combo') {
        if (spec.series.length < 2) {
            push('combo_series_count', 'series', 'Biểu đồ kết hợp cần ít nhất 2 series.');
        }
        spec.series.forEach((series, index) => {
            if (series.kind === null) {
                push('combo_kind_missing', `series.${index}.kind`, 'Biểu đồ kết hợp: mỗi series phải chọn vẽ cột ("bar") hay đường ("line").');
            }
        });
    }
    else {
        spec.series.forEach((series, index) => {
            if (series.axis === 'right') {
                push('right_axis_not_combo', `series.${index}.axis`, 'Trục phải chỉ dùng cho biểu đồ kết hợp.');
            }
        });
    }
    const seen = new Set();
    spec.series.forEach((series, index) => {
        const key = `${series.column}:${series.aggregate}`;
        if (seen.has(key)) {
            push('series_duplicate', `series.${index}`, `Series cột ${series.column} (${series.aggregate}) bị lặp.`);
        }
        seen.add(key);
    });
    return issues;
}
/**
 * Chữ ký để so hai biểu đồ trùng (plan §6): cùng loại + cùng cột nhãn + cùng
 * tập series (cột + cách gộp), không kể thứ tự series và chữ.
 */
export function chartSignature(spec) {
    const series = spec.series.map((item) => `${item.column}:${item.aggregate}`).sort();
    return `${spec.type}|${spec.category ?? '-'}|${[...new Set(series)].join(',')}`;
}
// ---------------------------------------------------------------------------
// Node `chart` trong tệp
// ---------------------------------------------------------------------------
export const ChartSource = z.object({
    /** `tableId` của node `table` (`data-table-id`) — so bằng chuỗi. */
    tableId: z.string().trim().min(1).max(64),
    /** `null` ⇒ **cả bảng**, co giãn theo bảng. Có vùng ⇒ địa chỉ cố định như công thức ô. */
    range: ChartRange.nullable(),
    /**
     * Hàng tiêu đề của **lưới đọc**: không xoay thì là số hàng đầu của bảng; xoay
     * (`transpose`) thì là số **cột** nhãn đầu của vùng — chúng thành hàng tiêu đề
     * của lưới đã xoay.
     */
    headerRows: z.number().int().min(0).max(20),
    /**
     * Đọc vùng **xoay** 90° — APM-CH-h: mỗi **hàng** của bảng là một series, tiêu đề
     * cột là nhãn (bảng "chỉ tiêu × tháng" của Excel). Mọi chỉ số cột trong `spec`
     * (`category`, `series[].column`) khi đó trỏ vào cột của lưới đã xoay, tính từ 0
     * ở đầu vùng. Tệp cũ không có trường này ⇒ `false`.
     */
    transpose: z.boolean().catch(false).default(false),
});
const SnapshotCell = z.union([z.string().max(500), z.number(), z.null()]);
/**
 * Bản chụp **dữ liệu đã tính** lần lưu gần nhất — cho markdown đọc được và để
 * vẽ khi mất nguồn. `columns[0]` là tên cột nhãn (`''` khi `category = null`),
 * sau đó mỗi series một tên; mỗi hàng là `[nhãn, ...giá trị]` của một điểm.
 */
export const ChartSnapshot = z.object({
    columns: z
        .array(z.string().max(200))
        .min(1)
        .max(CHART_MAX_SERIES + 1),
    rows: z.array(z.array(SnapshotCell).max(CHART_MAX_SERIES + 1)).max(CHART_MAX_POINTS),
    at: z.iso.datetime(),
    /** Đã cắt hàng cho vừa `MAX_CHART_SNAPSHOT_BYTES`. */
    truncated: z.boolean().catch(false).default(false),
});
const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const ChartAttrs = z.object({
    /** Spec hỏng (tệp của bản sau, sửa tay) ⇒ `null`: node hiện "Biểu đồ hỏng" thay vì làm hỏng tài liệu. */
    spec: ChartSpec.nullable().catch(null),
    /** `null` ⇒ biểu đồ tĩnh ("Giữ số hiện tại") — chỉ vẽ từ `snapshot`. */
    source: ChartSource.nullable().catch(null),
    snapshot: ChartSnapshot.nullable().catch(null),
    width: z
        .number()
        .int()
        .min(CHART_WIDTH.min)
        .max(CHART_WIDTH.max)
        .catch(CHART_WIDTH.default)
        .default(CHART_WIDTH.default),
    height: z
        .number()
        .int()
        .min(CHART_HEIGHT.min)
        .max(CHART_HEIGHT.max)
        .catch(CHART_HEIGHT.default)
        .default(CHART_HEIGHT.default),
    /** `null` ⇒ bảng màu mặc định (plan §5.2). Nhiều màu hơn series cho các lát bánh. */
    palette: z.array(HexColor).min(1).max(12).nullable().catch(null).default(null),
});
/** Số byte bản chụp chiếm trong tệp — so với `MAX_CHART_SNAPSHOT_BYTES`. */
export function chartSnapshotBytes(snapshot) {
    return new TextEncoder().encode(JSON.stringify(snapshot)).length;
}
/**
 * Cắt hàng cuối cho vừa `MAX_CHART_SNAPSHOT_BYTES` và ghi `truncated` (plan §3.3).
 * Vừa sẵn ⇒ trả nguyên. Tìm nhị phân số hàng giữ được — ≤ 8 lần đo với 200 hàng.
 */
export function capChartSnapshot(snapshot, maxBytes = MAX_CHART_SNAPSHOT_BYTES) {
    if (chartSnapshotBytes(snapshot) <= maxBytes)
        return snapshot;
    const withRows = (count) => ({
        ...snapshot,
        rows: snapshot.rows.slice(0, count),
        truncated: true,
    });
    let low = 0;
    let high = snapshot.rows.length - 1;
    while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        if (chartSnapshotBytes(withRows(middle)) <= maxBytes)
            low = middle;
        else
            high = middle - 1;
    }
    return withRows(low);
}
// ---------------------------------------------------------------------------
// Data insight — `POST /me/files/chart-plan` (skill `suggest_charts`, plan §6)
// ---------------------------------------------------------------------------
export const CHART_COLUMN_KINDS = [
    'number',
    'percent',
    'currency',
    'date',
    'category',
    'text',
    'empty',
];
export const ChartColumnKind = z.enum(CHART_COLUMN_KINDS);
/** Hồ sơ một cột của **cả bảng** (plan §4.3) — `profileColumns` ở web dựng. */
export const ChartColumnProfile = z.object({
    index: ColumnIndex,
    header: z.string().trim().max(200),
    kind: ChartColumnKind,
    /** Số ô có giá trị (không kể hàng tiêu đề). */
    filled: z.number().int().nonnegative(),
    distinct: z.number().int().nonnegative(),
    /** Chỉ cột đọc ra số; còn lại `null`. */
    min: z.number().nullable(),
    max: z.number().nullable(),
    sum: z.number().nullable(),
    samples: z.array(z.string().trim().max(60)).max(5),
    /** Đơn vị đọc được (`%`, `đ`, `$`, `tr`…) — gợi ý nhãn trục. */
    unit: z.string().trim().max(20).nullable(),
    /** Cột nằm trong vùng người dùng khoanh (`scope = 'table'` ⇒ luôn `true`). */
    inRange: z.boolean(),
});
const chartPlanShape = {
    count: z.number().int().min(CHART_PLAN_COUNT.min).max(CHART_PLAN_COUNT.max),
    /** Model phải biết người dùng **đã khoanh** vùng nào: `range` ⇒ chỉ dùng cột trong vùng. */
    scope: z.enum(['table', 'range']),
    range: ChartRange.nullable(),
    columns: z.array(ChartColumnProfile).min(1).max(CHART_MAX_TABLE_COLUMNS),
    /** TSV đã tính của vùng (cả bảng khi `scope = 'table'`), hàng đầu là tiêu đề. Không trim: tab đầu dòng là ô trống. */
    rows: z.string().max(CHART_PLAN_ROWS_MAX_CHARS),
    /** `rows` đã bị cắt đều cho vừa trần. */
    sampled: z.boolean(),
    /** Tiêu đề gần nhất phía trên bảng + đoạn ngay trước/sau bảng. */
    tableCaption: z.string().trim().max(CHART_PLAN_CAPTION_MAX),
    /** `chilldocToMarkdown` của cả tài liệu, bỏ chính bảng này, đã cắt. */
    document: z.string().trim().max(CHART_PLAN_DOCUMENT_MAX),
    /** Biểu đồ **đã có** trỏ vào bảng này — để đừng sinh trùng. */
    existing: z.array(ChartSpec).max(CHART_PLAN_EXISTING_MAX),
};
const scopeMatchesRange = (input, ctx) => {
    if ((input.scope === 'range') !== (input.range !== null)) {
        ctx.addIssue({
            code: 'custom',
            path: ['range'],
            message: '`scope = "range"` cần `range`; `scope = "table"` thì `range` là null.',
        });
    }
};
/** `params` của skill `suggest_charts` — phần body không kể `prompt`/`runId`. */
export const ChartPlanParams = z.object(chartPlanShape).superRefine(scopeMatchesRange);
/**
 * Body `POST /me/files/chart-plan`. `prompt`: yêu cầu thêm của người dùng — service
 * tách ra thành `AiRunRequest.prompt` như `DiagramGenerateInput`.
 */
export const ChartPlanInput = z
    .object({
    ...chartPlanShape,
    prompt: z.string().trim().max(CHART_PLAN_PROMPT_MAX).optional(),
    runId: z.uuid().optional(),
})
    .superRefine(scopeMatchesRange);
// ---------------------------------------------------------------------------
// Kiểu của biểu đồ do AI chọn — APM-CH-i (người dùng chốt 2026-09-29: mở rộng `suggest_charts`)
// ---------------------------------------------------------------------------
/**
 * Sáu màu của bảng màu biểu đồ (plan §5.2), **đúng thứ tự** của `DOC_CHART_PALETTE`
 * ở web. Model chọn **tên**, không mã hex: bảng màu đã kiểm mù màu và có bản cho
 * nền tối — một mã hex tự bịa thì không.
 */
export const CHART_COLOR_NAMES = [
    'blue',
    'orange',
    'green',
    'yellow',
    'pink',
    'dark_green',
];
export const ChartColorName = z.enum(CHART_COLOR_NAMES);
/** Cỡ (chiều cao) — con số ở `CHART_SIZE_HEIGHT`, nằm trong `CHART_HEIGHT`. */
export const CHART_SIZES = ['compact', 'standard', 'tall'];
export const ChartSize = z.enum(CHART_SIZES);
export const CHART_SIZE_HEIGHT = {
    compact: 220,
    standard: CHART_HEIGHT.default,
    tall: 420,
};
/** Bề ngang: cả cột chữ, hay một nửa (bánh nhỏ, vài cột) — số px do web tính từ cột chữ. */
export const CHART_WIDTH_MODES = ['full', 'half'];
export const ChartWidthMode = z.enum(CHART_WIDTH_MODES);
/**
 * Kiểu của một biểu đồ: màu từng series (theo thứ tự `series`), cỡ, bề ngang.
 * Mọi trường `null` được — web tự chọn theo luật tất định (`defaultChartStyle`).
 */
export const ChartStyle = z.object({
    colors: z.array(ChartColorName).min(1).max(CHART_MAX_SERIES).nullable(),
    size: ChartSize.nullable(),
    width: ChartWidthMode.nullable(),
});
/**
 * Một biểu đồ trong kế hoạch của model: spec + kiểu. `style` là trường **tuỳ chọn**
 * duy nhất của schema nộp — cố ý: nó chỉ là trang trí, model quên thì web tự chọn,
 * không đáng một vòng `finalize` trả lại. Không lưu vào node: web đổi ra `palette`,
 * `width`, `height` của `ChartAttrs` lúc chèn.
 */
export const ChartPlanChart = ChartSpec.extend({ style: ChartStyle.nullable().optional() });
/**
 * Schema nộp kết quả của model. `note`: vì sao ít hơn `count` (bắt buộc khi ít hơn —
 * `finalize`). `findings`: **điểm chạm dữ liệu** (APM-CH-i, "làm như một DA") — 2–5
 * phát hiện có con số đọc từ bảng, xếp theo mức đáng chú ý; web hiện trên lưới xem
 * trước và chèn được thành ghi chú dưới biểu đồ. **Bắt buộc** (null được): lượt
 * live đầu tiên (2026-09-29) để tuỳ chọn thì model bỏ qua ở cả 5/5 case dù prompt
 * đòi — đúng cạm bẫy "trường tuỳ chọn là chỗ model hay bỏ sót" ghi ở đầu file.
 */
export const ChartPlan = z.object({
    charts: z.array(ChartPlanChart).max(CHART_PLAN_COUNT.max),
    note: z.string().trim().min(1).max(CHART_PLAN_NOTE_MAX).nullable(),
    findings: z
        .array(z.string().trim().min(1).max(CHART_FINDING_MAX))
        .max(CHART_FINDINGS_MAX)
        .nullable(),
});
export const ChartPlanResult = ChartPlan.extend({
    /** Chuỗi thập phân như mọi số tiền AI khác — không làm tròn qua `number`. */
    costUsd: UsdAmount,
    partial: z.boolean(),
});
//# sourceMappingURL=chilldoc-chart.js.map